import React, { useState } from "react";
import { ChevronDown, ShieldCheck, MessageSquare } from "lucide-react";

import SectionLabel from "./SectionLabel";
import { CONNECTORS } from "./aigoMetrics";

const faqs = [
  {
    q: "Is my store safe? What if AIGO ships a bad change?",
    a: "Every action runs in preview_mode by default — nothing touches your store until you approve it. When you approve, you see the exact change first (screenshot + diff). And every implementation can be undone in one click, including edits to Shopify products, Google/Meta campaigns, and Klaviyo automations.",
  },
  {
    q: "What data does AIGO read? Do you share or sell it?",
    a: "AIGO reads read-only from Shopify (orders, products, customers), Google Ads + GSC (campaign & search data), Meta Ads, GA4, and Klaviyo — only the scopes you approve during OAuth. We never sell your data. We never train third-party foundation models on it. Full details in our Privacy Policy.",
  },
  {
    q: "Can I cancel anytime? Any long-term contracts?",
    a: "Cancel anytime from your account or by uninstalling the Shopify app. You keep access through the end of your current billing period. Annual plans include a 30-day money-back window if it doesn't pay for itself.",
  },
  {
    q: "Which platforms does AIGO connect to?",
    a: `Shopify (required), plus ${CONNECTORS.filter(c => c !== "Shopify").join(", ")}. All connectors are OAuth-based, take under 60 seconds each, and can be revoked from your platform admin at any time.`,
  },
  {
    q: "What's the difference between Free and Pro?",
    a: "Free shows every $-Leak, every Untapped opportunity, and the exact $/mo impact of each — you'll see the full +$76.8k on the table. Pro unlocks 1-click Implement, Approve-all-safe, Adjust-Impact sliders, Autonomous Mode, all Operator Modules (Image Engine, SEO Sanitizer, Inventory Guard), and the AI Profit Copilot chat.",
  },
  {
    q: "Do I need to know finance or ad-buying to use this?",
    a: "No. Every recommendation arrives in plain English (\"ROAS down 12% on Google Ads — bleed keyword found\") with the exact $/mo impact and a HIGH/MEDIUM confidence tag. If you can approve a Slack message, you can operate AIGO.",
  },
];

export const FAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="px-6 md:px-10 py-20 md:py-24 border-t border-slate-100"
    >
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <SectionLabel><MessageSquare className="w-3 h-3" /> FAQ</SectionLabel>
          <h2 className="text-3xl md:text-5xl text-slate-900 mt-4 mb-4 tracking-tight font-bold leading-[1.05]">
            Happy to answer <span className="text-emerald-600">your questions.</span>
          </h2>
          <p className="text-slate-600 text-base md:text-lg leading-relaxed">
            Everything you’d want to know before connecting your store.
          </p>
        </div>

        <div className="space-y-3">
          {faqs.map((f, i) => {
            const isOpen = open === i;
            return (
              <div
                key={i}
                data-testid={`faq-item-${i}`}
                className={`rounded-2xl border transition ${
                  isOpen ? "border-emerald-200 bg-white shadow-md shadow-emerald-500/5"
                         : "border-slate-200 bg-white hover:border-emerald-300"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  data-testid={`faq-toggle-${i}`}
                  className="w-full flex items-center justify-between text-left px-5 md:px-6 py-4 md:py-5 gap-4"
                >
                  <span className="text-slate-900 text-base md:text-lg font-bold tracking-tight leading-snug">
                    {f.q}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-slate-400 shrink-0 transition-transform ${
                      isOpen ? "rotate-180 text-emerald-600" : ""
                    }`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${
                    isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                  }`}
                >
                  <div className="px-5 md:px-6 pb-5 md:pb-6 text-slate-600 text-sm md:text-base leading-relaxed">
                    {f.a}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-8 text-center text-sm text-slate-500 inline-flex items-center gap-2 w-full justify-center">
          <ShieldCheck className="w-4 h-4 text-emerald-600" />
          Safe by default · undo anytime · your data stays yours.
        </div>
      </div>
    </section>
  );
};

export default FAQ;
